import React, { useState } from "react";
import { Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { API_BASE_URL } from "../../../config";

// Default Leaflet icon
const defaultIcon = new L.Icon({
    iconUrl: require("leaflet/dist/images/marker-icon.png"),
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
    shadowSize: [41, 41],
});

// CSS for custom icon and popup
const iconStyles = (vehicleLocation, zoomedIn) => {
    return `
        <div style="position: relative; text-align: center;">
            ${
        zoomedIn
            ? `<div style="
                            position: absolute; 
                            bottom: 50px; 
                            left: 50%; 
                            width: 120px;
                            transform: translateX(-50%); 
                            background-color: rgba(255, 255, 255, 0.7); 
                            padding: 2px 5px; 
                            border: 1px solid #ff0059;
                            border-radius: 4px; 
                            font-size: 13px; 
                            font-weight: semi*; 
                            color: #ff0059;
                            box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.3);
                            z-index: 1000;
                            white-space: nowrap;
                            overflow: hidden;
                            text-overflow: ellipsis;
                        ">
                        ${vehicleLocation.nameuser || "Unknown Vehicle"}
                        </div>`
            : ""
    }
            <div style="
    transform: rotate(${(vehicleLocation.heading || 0) % 360 - 90}deg) scaleY(${vehicleLocation.heading > 180 ? -1 : 1});
    width: 55px;
    height: 45px;
    display: inline-block;
    background: url('/ice-truck.png') no-repeat center;
    background-size: contain;
"></div>
        </div>
    `;
};

const WAKE_COOLDOWN_MS = 2000;

// Auth header pattern matches HardwareV3's fetchData() in Source/Dev/HardWare.js
// (raw fetch, not the Utils/useFetchData hook — that hook always calls
// response.json(), which throws on the empty/204 body this endpoint may return).
const requestDeviceWake = async (userUid) => {
    const token = JSON.parse(localStorage.getItem("user"))?.token;
    const response = await fetch(
        `${API_BASE_URL}/gps/api/v1/gps/device/${userUid}/wake`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }
    );
    if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
    }
};

const WakeButton = ({ userUid }) => {
    const [sending, setSending] = useState(false);
    const [feedback, setFeedback] = useState(null); // { type: 'success' | 'error', text }

    const handleClick = async () => {
        if (sending) return;
        setSending(true);
        setFeedback(null);
        try {
            await requestDeviceWake(userUid);
            setFeedback({ type: "success", text: "ส่งคำสั่งปลุกหน้าจอแล้ว" });
        } catch (err) {
            setFeedback({ type: "error", text: `ส่งคำสั่งไม่สำเร็จ (${err.message})` });
        } finally {
            setTimeout(() => setSending(false), WAKE_COOLDOWN_MS);
        }
    };

    return (
        <>
            <i
                className={`fa-solid ${sending ? "fa-spinner fa-spin" : "fa-mobile-screen-button"}`}
                title="ปลุกหน้าจออุปกรณ์"
                style={{
                    cursor: sending ? "default" : "pointer",
                    fontSize: "20px",
                    color: sending ? "#aaa" : "gray",
                    marginLeft: "10px",
                }}
                onClick={sending ? undefined : handleClick}
            ></i>
            {feedback && (
                <div
                    style={{
                        marginTop: "4px",
                        fontSize: "12px",
                        color: feedback.type === "success" ? "#2e7d32" : "#c62828",
                        textAlign: "right",
                    }}
                >
                    {feedback.text}
                </div>
            )}
        </>
    );
};

const VehicleMarkers = ({ vehicleLocations, zoomedIn, tracking }) => {
    // Normalize data: ensure `vehicleLocations` is always an array
    if (!vehicleLocations) return null;

    const normalizedLocations = Array.isArray(vehicleLocations)
        ? vehicleLocations
        : [vehicleLocations];

    return normalizedLocations.map((vehicleLocation) => {
        if (!vehicleLocation || !isFinite(vehicleLocation.latitude) || !isFinite(vehicleLocation.longitude)) return null;

        // Set custom icon for each vehicle
        const customIcon = L.divIcon({
            html: iconStyles(vehicleLocation, zoomedIn),
            className: "custom-icon",
            iconSize: [25, 41],
            iconAnchor: [12, 30],
            popupAnchor: [0, -45],
        });


        return (
            <Marker
                key={vehicleLocation.id}
                position={[vehicleLocation.latitude, vehicleLocation.longitude]}
                icon={customIcon}
                zIndexOffset={1000}
            >
                <Popup>
                    <div style={popupStyles}>
                        <h3 style={headerStyle}>
                            {vehicleLocation.nameuser || "Unknown Vehicle"}
                        </h3>
                        <p style={textStyle}><strong>ID:</strong> {vehicleLocation.username || "Unknown User"}</p>
                        <p style={textStyle}><strong>Latitude:</strong> {vehicleLocation.latitude}</p>
                        <p style={textStyle}><strong>Longitude:</strong> {vehicleLocation.longitude}</p>
                        <p style={textStyle}><strong>Heading:</strong> {vehicleLocation.heading}</p>
                        <p style={textStyle}><strong>Speed:</strong> {vehicleLocation.speed}</p>
                        <p style={textStyle}><strong>Battery:</strong> {vehicleLocation.battery} %</p>
                        <p style={textStyle}><strong>Charging:</strong> {vehicleLocation.charge ? 'Yes' : 'No'}</p>
                        <p style={textStyle}><strong>Time:</strong> {vehicleLocation.time || "N/A"}</p>
                        <p style={textStyle}><strong>Date:</strong> {vehicleLocation.date || "N/A"}</p>

                        <div style={{ textAlign: "right" }}>
                            <i
                                className="fa-solid fa-street-view"
                                style={{ cursor: "pointer", fontSize: "20px", color: "gray" }}
                                onClick={() => {
                                    const mapUrl = `https://www.google.com/maps/place/${vehicleLocation.latitude}+${vehicleLocation.longitude}/@${vehicleLocation.latitude},${vehicleLocation.longitude},150m`;
                                    window.open(mapUrl, "_blank");
                                }}
                            ></i>
                            <WakeButton userUid={vehicleLocation.username} />
                        </div>
                    </div>
                </Popup>
            </Marker>
        );
    });
};

// Popup Styles
const popupStyles = {
    maxWidth: "250px",
    fontFamily: "Arial, sans-serif",
    fontSize: "14px",
    color: "#333",
};

// Header Style
const headerStyle = {
    margin: "0 0 10px 0",
    fontSize: "16px",
    color: "#0275d8",
    textAlign: "center",
};

// Text Style
const textStyle = {
    margin: "5px 0"
};

export default VehicleMarkers;
